import { createClient } from '@supabase/supabase-js';

const url = process.env.REACT_APP_SUPABASE_URL as string;
const key = process.env.REACT_APP_SUPABASE_ANON_KEY as string;

export const supabase = createClient(url, key);

// Visits are keyed to the app's users table, not Supabase Auth ids.
// Look up (or create) the users row matching the logged-in email.
export async function getAppUserId(email: string): Promise<string | null> {
  const { data } = await supabase
    .from('users')
    .select('id')
    .eq('email', email)
    .maybeSingle();
  if (data) return data.id;

  const { data: created, error } = await supabase
    .from('users')
       .insert({ email })
    .select('id')
    .single();
  if (error) {
    console.error('Could not create users row:', error.message);
    return null;
  }
  return created.id;
}

// Three states: active (paid and current), lapsed (paid before, now expired),
// none (never paid). Lapsed members keep read-only access to their passport.
export type MembershipStatus = 'active' | 'lapsed' | 'none';

export async function getMembershipStatus(appUserId: string): Promise<MembershipStatus> {
  const { data } = await supabase
    .from('users')
    .select('member_until')
    .eq('id', appUserId)
    .maybeSingle();
  if (!data?.member_until) return 'none';
  return new Date(data.member_until).getTime() > Date.now() ? 'active' : 'lapsed';
}

// Returns true if the member's paid membership is current
export async function isMembershipActive(appUserId: string): Promise<boolean> {
  const { data } = await supabase
    .from('users')
    .select('member_until')
    .eq('id', appUserId)
    .maybeSingle();
  if (!data?.member_until) return false;
  return new Date(data.member_until).getTime() > Date.now();
}
